import { Avatar, Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { Icons, ItemImageArea, SkeletonText } from 'utoniq-ui'

import { Maybe, StoreItemCompleteFreeQuery } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

export * from './fragments'

export type State = {
  artistId: string
  itemId: string
  loading: boolean
  portfolio: Maybe<StoreItemCompleteFreeQuery['portfolio']>
  isFreeItem: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const StoreItemCompleteFreeTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('storeItemPurchaseComplete')
  const { generateQueryPath, generatePath, routerPages, routerNames } =
    useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <BaseLayout
        header={{
          title: state.isFreeItem ? t('obtainTitle') : t('purchaseTitle'),
        }}
        margin
      >
        <Head title={routerNames.StoreItemPurchaseComplete} />
        <Stack alignItems="center" sx={{ pt: 2, pb: 5 }}>
          {state.loading ? (
            <Box sx={{ width: 'fit-content' }}>
              <Skeleton variant="rectangular" width={175} height={280} />
              <Stack alignItems="center" sx={{ mt: 5 }}>
                <SkeletonText variant="large" width="50%" />
              </Stack>
              <Stack alignItems="center" sx={{ mt: 1 }}>
                <SkeletonText variant="small" width="100%" />
              </Stack>
              <Stack alignItems="center" sx={{ mt: 4 }}>
                <SkeletonText variant="medium" width="50%" />
              </Stack>
            </Box>
          ) : (
            <>
              <Stack alignItems="center">
                <ItemImageArea
                  size="large"
                  src={state.portfolio?.item?.thumbnail?.front?.mediumUri ?? ''}
                />
                <Stack alignItems="center" spacing={1} sx={{ pb: 2, pt: 5 }}>
                  <Typography variant="h4">
                    {state.portfolio?.item?.title}
                  </Typography>
                  {!!state.portfolio?.item?.sku?.unitName && (
                    <Typography variant="h6">
                      {state.portfolio?.item?.sku?.unitName}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" justifyContent="center" spacing={4}>
                  {state.portfolio?.item.rarity && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="overline" color="text.hint">
                        {t('rarity')}
                      </Typography>
                      {state.portfolio?.item.rarityIconImage?.smallUri && (
                        <Avatar
                          variant="square"
                          src={state.portfolio?.item.rarityIconImage.smallUri}
                          sx={{ height: 12, width: 12 }}
                        />
                      )}
                      <Typography variant="overline">
                        {state.portfolio?.item.rarity?.name
                          ? t(state.portfolio?.item?.rarity.name)
                          : '-'}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="overline" color="text.hint">
                      {t('possessionNumber')}
                    </Typography>
                    <Typography variant="overline">
                      {state.portfolio?.number ?? 0}
                      {!state.portfolio?.item.hideSalesNumber && (
                        <>/{state.portfolio?.item.issuedNumber ?? '∞'}</>
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Stack flex={1} alignItems="center" sx={{ pt: 6 }}>
                <Button
                  variant="outlined"
                  sx={{ width: 'fit-content', px: 4, py: 1 }}
                  href={generatePath(routerPages.ItemComment, {
                    itemId: state.itemId,
                  })}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icons.Reply color="inherit" fontSize="small" />
                    <Typography variant="button">
                      {state.portfolio?.item?.commentCount ?? 0}
                    </Typography>
                  </Stack>
                </Button>
              </Stack>
            </>
          )}
        </Stack>
        <Stack alignItems="center" sx={{ p: 5 }} spacing={4}>
          <Button
            size="large"
            sx={{ width: 244 }}
            href={generateQueryPath(
              routerPages.OwnedItemDetail,
              { itemId: state.itemId },
              { skuId: encodeSkuId(state.portfolio?.item?.sku?.id ?? '') }
            )}
          >
            {t('checkTheItem')}
          </Button>
          <Button
            href={generatePath(routerPages.ArtistStore, {
              artistId: state.artistId,
            })}
            size="large"
            variant="text"
            color="primary"
            sx={{ width: 244 }}
          >
            {t('continueToBuy')}
          </Button>
          <Button
            href={generatePath(routerPages.ArtistTop, {
              artistId: state.artistId,
            })}
            size="large"
            variant="text"
            color="primary"
            sx={{ width: 244 }}
          >
            {t('toTop')}
          </Button>
        </Stack>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
